/**
 * One lien, rendered (#561). Shared by the Heftelser tab and the inline block
 * on Kjøretøy med eier, so the two can never describe the same lien
 * differently.
 *
 * The design decision that matters: a salgspant and an utleggspant do not look
 * alike. One is financing — ordinary, expected, discharged at sale. The other
 * is the namsmann acting on an unpaid debt, which says something about the
 * counterparty and can block the sale outright. Rendering both as "a lien with
 * an amount" would flatten the single most useful distinction in the data.
 */
import { AlertTriangle, Banknote, Gavel, Car } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import i18n from '../../i18n'
import { formatNorwegianDate } from '../../utils/dateUtils'
import type { Heftelse, HeftelsePart } from './oppslagApi'

const STIL = {
  finansiering: {
    ramme: 'border-blue-400 bg-blue-50 dark:bg-blue-950/30 dark:border-blue-600',
    tekst: 'text-blue-800 dark:text-blue-300',
    Ikon: Banknote,
  },
  tvang: {
    ramme: 'border-red-500 bg-red-50 dark:bg-red-950/30 dark:border-red-600',
    tekst: 'text-red-800 dark:text-red-300',
    Ikon: Gavel,
  },
  annet: {
    ramme: 'border-gray-400 bg-gray-50 dark:bg-gray-900/40 dark:border-gray-600',
    tekst: 'text-gray-700 dark:text-gray-300',
    Ikon: AlertTriangle,
  },
}

export function formaterBelop(belop: number | null, valuta: string | null): string {
  // Absent is not zero. Leasing records carry no amount at all, and "0 kr"
  // would assert there is no money involved.
  if (belop === null || belop === undefined) return i18n.t('heftelse.notStated', { ns: 'oppslag' })
  return `${belop.toLocaleString('nb-NO', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })} ${valuta ?? ''}`.trim()
}

function PartLinje({ etikett, part }: { etikett: string; part: HeftelsePart }) {
  const { t } = useTranslation('oppslag')
  return (
    <div className="text-sm">
      <span className="text-gray-500 dark:text-gray-400">{etikett}: </span>
      <span className="text-gray-900 dark:text-gray-100 font-medium">
        {part.navn ?? '—'}
      </span>
      {part.organisasjonsnummer && (
        <span className="text-xs text-gray-500 dark:text-gray-400">
          {' '}{t('felles.orgnr', { orgnr: part.organisasjonsnummer })}
        </span>
      )}
      {(part.postnummer || part.poststed) && (
        <span className="text-xs text-gray-500 dark:text-gray-400">
          {' '}· {[part.postnummer, part.poststed].filter(Boolean).join(' ')}
        </span>
      )}
    </div>
  )
}

export default function HeftelseKort({ heftelse }: { heftelse: Heftelse }) {
  const { t } = useTranslation('oppslag')
  const stil = STIL[heftelse.kategori] ?? STIL.annet
  const { Ikon } = stil

  return (
    <div
      className={`border-l-4 rounded-sm p-3 space-y-1 ${stil.ramme}`}
      data-testid={`heftelse-${heftelse.kategori}`}
    >
      <div className="flex items-center gap-2 flex-wrap">
        <Ikon className={`w-4 h-4 ${stil.tekst}`} />
        <span className={`text-sm font-bold ${stil.tekst}`}>{heftelse.type}</span>
        {heftelse.kategori === 'tvang' && (
          <span className="text-xs font-bold uppercase tracking-wide text-red-700 dark:text-red-300">
            {t('heftelse.tvang')}
          </span>
        )}
        {!heftelse.aktiv && (
          // A deleted lien is history. Shown, but never as an encumbrance.
          <span
            className="text-xs px-1.5 py-0.5 rounded-sm bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300"
            data-testid="heftelse-ikke-aktiv"
          >
            {heftelse.status ?? t('heftelse.notRegistered')}
          </span>
        )}
        {heftelse.tinglyst && (
          <span className="text-xs text-gray-500 dark:text-gray-400">
            {t('heftelse.registered', { dato: formatNorwegianDate(heftelse.tinglyst) })}
          </span>
        )}
      </div>

      <div className={`text-lg font-semibold ${stil.tekst}`}>
        {formaterBelop(heftelse.belop, heftelse.valuta)}
      </div>

      {heftelse.rettighetshaver && (
        <PartLinje
          etikett={heftelse.rettighetshaver_etikett}
          part={heftelse.rettighetshaver}
        />
      )}
      {heftelse.forpliktede.map((f, i) => (
        <PartLinje key={i} etikett={heftelse.forpliktet_etikett} part={f} />
      ))}

      {heftelse.namsmyndighet && (
        <div className="text-sm">
          <span className="text-gray-500 dark:text-gray-400">{t('heftelse.namsmyndighet')}</span>
          <span className="text-gray-900 dark:text-gray-100">
            {heftelse.namsmyndighet}
          </span>
          {heftelse.prosessfullmektig && (
            <span className="text-xs text-gray-500 dark:text-gray-400">
              {' '}{t('heftelse.via', { navn: heftelse.prosessfullmektig })}
            </span>
          )}
        </div>
      )}

      {heftelse.krav_type && (
        <div className="text-xs text-gray-500 dark:text-gray-400">
          {t('heftelse.krav', { krav: heftelse.krav_type })}
        </div>
      )}

      {heftelse.andre_kjoretoy.length > 0 && (
        // A production utleggspant covered eight vehicles. Its amount belongs
        // to the lien as a whole, not to whichever of the eight was searched
        // for — and reading it as this car's debt is wrong in both directions.
        <div
          className="flex items-start gap-1.5 text-xs text-amber-800 dark:text-amber-300 pt-1"
          data-testid="heftelse-flere-kjoretoy"
        >
          <Car className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          <span>
            {t('heftelse.flereKjoretoy', { n: heftelse.antall_kjoretoy })}{' '}
            <span className="font-mono">
              {heftelse.andre_kjoretoy.join(', ')}
            </span>
          </span>
        </div>
      )}
    </div>
  )
}
